<script lang="ts">
  interface Reply {
    id: string;
    threads_reply_id: string;
    author_username: string;
    author_display_name: string | null;
    author_avatar_url: string | null;
    content: string;
    likes_count: number;
    depth: number;
    replies_count: number;
    parent_reply_id: string | null;
    created_at: string;
    posted_at: string | null;
    threads_url: string | null;
    dm_prepared_at?: string | null;
    metadata?: Record<string, unknown>;
    post_content_preview: string;
  }

  let { reply, child_replies = [] } = $props<{ reply: Reply; child_replies?: Reply[] }>();

  const profileUrl = `https://www.threads.net/@${reply.author_username}`;
  const childProfileUrl = (u: string) => `https://www.threads.net/@${u}`;

  const SURVEY_URL = 'https://ax-admin-z42x.onrender.com/vibe_study/survey/vibestudy-pilot-1';
  let dmPreparedAt = $state(reply.dm_prepared_at ?? null);

  async function callMark(mode: 'mark' | 'unmark') {
    try {
      const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';
      const res = await fetch(`/admin/community/comments/${reply.id}/mark_dm_prepared`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrf, Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ action_mode: mode })
      });
      if (res.ok) {
        const data = await res.json();
        dmPreparedAt = data.dm_prepared_at ?? null;
      }
    } catch (_e) { /* noop */ }
  }

  async function prepareDm() {
    const msg = `@${reply.author_username}님 안녕하세요! 여의도 VibeStudy 모집글에 댓글 감사합니다 🙌\n반 배정용 5분 설문 응답 부탁드려요:\n${SURVEY_URL}`;
    try {
      await navigator.clipboard.writeText(msg);
    } catch (_e) {
      alert('클립보드 복사 실패. 메시지를 수동 복사하세요:\n\n' + msg);
    }
    window.open(profileUrl, '_blank', 'noopener');
    await callMark('mark');
  }

  async function undoDm() {
    if (!confirm(`DM 전송 표시를 취소하시겠어요?`)) return;
    await callMark('unmark');
  }
</script>

<div class="p-6 max-w-3xl space-y-6">
    <a href="/admin/community/comments" class="text-sm text-gray-600">← 목록</a>

    <header class="flex gap-4 items-start">
      <a href={profileUrl} target="_blank" rel="noopener"
         class="shrink-0 hover:opacity-80 transition-opacity"
         aria-label="Threads 프로필 열기">
        {#if reply.author_avatar_url}
          <img src={reply.author_avatar_url} class="w-16 h-16 rounded-full" alt="" />
        {:else}
          <div class="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-xl font-bold">
            {reply.author_username[0]?.toUpperCase() ?? '?'}
          </div>
        {/if}
      </a>
      <div>
        <a href={profileUrl} target="_blank" rel="noopener"
           class="text-xl font-bold text-gray-900 hover:text-blue-600 hover:underline">
          @{reply.author_username} ↗
        </a>
        {#if reply.author_display_name}
          <p class="text-gray-600">{reply.author_display_name}</p>
        {/if}
        <p class="text-sm text-gray-400">{new Date(reply.created_at).toLocaleString('ko')}</p>
      </div>
    </header>

    <div class="p-4 bg-gray-50 rounded">
      <div class="text-xs text-gray-500 mb-2">원본 게시물</div>
      <div>{reply.post_content_preview}</div>
    </div>

    <div class="p-4 border rounded whitespace-pre-wrap">{reply.content}</div>

    <dl class="grid grid-cols-4 gap-4 text-sm">
      <div><dt class="text-gray-500">좋아요</dt><dd>{reply.likes_count}</dd></div>
      <div><dt class="text-gray-500">깊이</dt><dd>{reply.depth}</dd></div>
      <div><dt class="text-gray-500">답글 수</dt><dd>{reply.replies_count}</dd></div>
      <div><dt class="text-gray-500">Threads ID</dt><dd class="font-mono text-xs break-all">{reply.threads_reply_id}</dd></div>
    </dl>

    <div class="flex gap-2 flex-wrap">
      <button onclick={prepareDm}
              class="inline-flex items-center gap-1 px-4 py-2 rounded font-medium
                     {dmPreparedAt
                       ? 'bg-green-100 text-green-800 hover:bg-green-200'
                       : 'bg-gray-900 text-white hover:bg-gray-700'}"
              title="메시지를 클립보드에 복사하고 Threads 프로필을 새 탭으로 엽니다">
        {dmPreparedAt ? '✓ DM 전송됨 — 다시 복사' : '💌 DM 준비 (메시지 복사 + 프로필 열기)'}
      </button>
      {#if dmPreparedAt}
        <button onclick={undoDm}
                class="inline-flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded"
                title="DM 전송 표시 취소">
          ✕ 취소
        </button>
      {/if}
      <a href={profileUrl} target="_blank" rel="noopener"
         class="inline-flex items-center gap-1 px-4 py-2 bg-gray-100 text-gray-800 rounded hover:bg-gray-200">
        👤 프로필만 ↗
      </a>
      {#if reply.threads_url}
        <a href={reply.threads_url} target="_blank" rel="noopener"
           class="inline-flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          💬 이 댓글 원문 ↗
        </a>
      {/if}
    </div>
    {#if dmPreparedAt}
      <p class="text-xs text-gray-500">
        DM 준비 시각: {new Date(dmPreparedAt).toLocaleString('ko')}
      </p>
    {/if}

    {#if child_replies.length > 0}
      <section class="mt-8">
        <h2 class="text-lg font-bold mb-3 flex items-center gap-2">
          💬 답글 <span class="text-sm font-normal text-gray-500">({child_replies.length})</span>
        </h2>
        <ul class="space-y-3 border-l-2 border-gray-200 pl-4">
          {#each child_replies as c (c.id)}
            <li class="bg-white border rounded p-3">
              <div class="flex items-start gap-3">
                <a href={childProfileUrl(c.author_username)} target="_blank" rel="noopener"
                   class="shrink-0 hover:opacity-80">
                  {#if c.author_avatar_url}
                    <img src={c.author_avatar_url} class="w-10 h-10 rounded-full" alt="" />
                  {:else}
                    <div class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold text-sm">
                      {c.author_username[0]?.toUpperCase() ?? '?'}
                    </div>
                  {/if}
                </a>
                <div class="flex-1 min-w-0">
                  <div class="flex items-baseline gap-2 flex-wrap">
                    <a href={childProfileUrl(c.author_username)} target="_blank" rel="noopener"
                       class="font-medium text-sm hover:text-blue-600 hover:underline">
                      @{c.author_username} ↗
                    </a>
                    <span class="text-xs text-gray-400">
                      {c.posted_at ? new Date(c.posted_at).toLocaleString('ko') : ''}
                    </span>
                    {#if c.replies_count > 0}
                      <span class="text-xs px-1.5 py-0.5 bg-gray-100 rounded">답글 {c.replies_count}</span>
                    {/if}
                  </div>
                  <p class="text-sm mt-1 whitespace-pre-wrap">{c.content}</p>
                  <div class="mt-2 flex gap-3 text-xs">
                    <a href={`/admin/community/comments/${c.id}`}
                       class="text-blue-600 hover:underline">상세 →</a>
                    {#if c.threads_url}
                      <a href={c.threads_url} target="_blank" rel="noopener"
                         class="text-gray-500 hover:underline">원문 ↗</a>
                    {/if}
                  </div>
                </div>
              </div>
            </li>
          {/each}
        </ul>
      </section>
    {/if}
  </div>
